import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Plus, Trash2, AlertCircle, CheckCircle2, Smile, Pill, AlertTriangle, UtensilsCrossed, Moon, Heart, Sparkles, Loader2, Clock, X, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppPlaceholder from '../components/WhatsAppPlaceholder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorities = [
  { value: 'high', label: 'High Priority', color: 'bg-red-100 text-red-700' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'low', label: 'Low Priority', color: 'bg-gray-100 text-gray-700' },
];

const forWhomOptions = [
  { value: 'asisten', label: 'Asisten' },
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'driver', label: 'Driver' },
  { value: 'other', label: 'Other' },
];

const moodOptions = [
  { value: 'happy', label: '😊 Happy', color: 'bg-green-100 text-green-700' },
  { value: 'calm', label: '😌 Calm', color: 'bg-blue-100 text-blue-700' },
  { value: 'tired', label: '😴 Tired', color: 'bg-purple-100 text-purple-700' },
  { value: 'fussy', label: '😤 Fussy', color: 'bg-orange-100 text-orange-700' },
  { value: 'sick', label: '🤒 Sick', color: 'bg-red-100 text-red-700' },
];

const HandoffBoardPage = () => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('quick');
  
  // Section 3C: Main view mode toggle (for_someone vs to_self)
  const [viewMode, setViewMode] = useState('for_someone'); // for_someone | to_self
  
  // Section 3C: Note to Self state
  const [selfMood, setSelfMood] = useState('');
  const [selfNotes, setSelfNotes] = useState('');
  const [selfPriorities, setSelfPriorities] = useState('');
  const [writingSelf, setWritingSelf] = useState(false);
  const [generatedSelfNote, setGeneratedSelfNote] = useState(null);
  const [selfNoteCallId, setSelfNoteCallId] = useState(null);
  const [reminderTime, setReminderTime] = useState('06:30');
  
  const [newItem, setNewItem] = useState({
    what: '',
    when: '',
    how: '',
    special_notes: '',
    priority: 'normal',
    for_whom: 'asisten',
    is_completed: false,
    // Structured fields
    child_mood: '',
    medications: [],
    triggers_today: '',
    last_meal: '',
    nap_info: '',
    handoff_type: 'to_person'
  });
  const [newMed, setNewMed] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/handoff`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching handoff items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/handoff`, newItem);
      toast.success('Handoff item added!');
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const resetForm = () => {
    setNewItem({
      what: '', when: '', how: '', special_notes: '',
      priority: 'normal', for_whom: 'asisten', is_completed: false,
      child_mood: '', medications: [], triggers_today: '', last_meal: '', nap_info: '',
      handoff_type: 'to_person'
    });
    setNewMed('');
  };

  // HITL confirmation helper
  const confirmAIAction = async (callId, userAction, editDelta = []) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Section 3C: Generate self note
  const handleWriteSelfNote = async () => {
    if (!selfMood || !selfPriorities) {
      toast.error('Please fill in mood and tomorrow priorities');
      return;
    }
    
    setWritingSelf(true);
    try {
      const response = await axios.post(`${API}/ai/handoff/write-self`, {
        mood: selfMood,
        notes: selfNotes,
        tomorrow_priorities: selfPriorities
      });
      setGeneratedSelfNote(response.data.note_text);
      setSelfNoteCallId(response.data.call_id);
    } catch (error) {
      toast.error('Failed to generate note');
    } finally {
      setWritingSelf(false);
    }
  };

  // Section 3C: Save self note as handoff
  const saveSelfNote = async (withReminder = false) => {
    if (!generatedSelfNote) return;
    
    try {
      await axios.post(`${API}/handoff`, {
        what: generatedSelfNote,
        when: withReminder ? `Reminder at ${reminderTime}` : 'Tomorrow morning',
        how: '',
        special_notes: `Mood: ${selfMood}. Priorities: ${selfPriorities}`,
        priority: 'normal',
        for_whom: 'self',
        is_completed: false,
        handoff_type: 'to_self',
        scheduled_reminder_time: withReminder ? reminderTime : null
      });
      
      await confirmAIAction(selfNoteCallId, 'accepted');
      
      toast.success(withReminder ? 'Note saved with reminder!' : 'Note saved!');
      setGeneratedSelfNote(null);
      setSelfNoteCallId(null);
      setSelfMood('');
      setSelfNotes('');
      setSelfPriorities('');
      fetchItems();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  // Section 3C: Discard self note
  const discardSelfNote = async () => {
    await confirmAIAction(selfNoteCallId, 'ignored');
    setGeneratedSelfNote(null);
    setSelfNoteCallId(null);
    toast.info('Note discarded');
  };

  const addMedication = () => {
    if (newMed.trim()) {
      setNewItem({...newItem, medications: [...(newItem.medications || []), newMed.trim()]});
      setNewMed('');
    }
  };

  const removeMedication = (index) => {
    const meds = [...(newItem.medications || [])];
    meds.splice(index, 1);
    setNewItem({...newItem, medications: meds});
  };

  const toggleComplete = async (item) => {
    try {
      await axios.put(`${API}/handoff/${item.id}`, { ...item, is_completed: !item.is_completed });
      fetchItems();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/handoff/${id}`);
      toast.success('Item removed');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const pendingItems = items.filter(i => !i.is_completed);
  const completedItems = items.filter(i => i.is_completed);

  const getPriorityInfo = (priority) => priorities.find(p => p.value === priority) || priorities[1];

  const groupByForWhom = (itemList) => {
    return itemList.reduce((acc, item) => {
      const forWhom = item.for_whom || 'other';
      if (!acc[forWhom]) acc[forWhom] = [];
      acc[forWhom].push(item);
      return acc;
    }, {});
  };

  return (
    <div className="space-y-6" data-testid="handoff-board-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-pink-600" />
            </div>
            Handoff Board
          </h1>
          <p className="text-nurture-muted mt-1">Instructions for helpers, spouse, or yourself</p>
        </div>
      </div>

      {/* Section 3C: Mode Toggle */}
      <div className="flex justify-center">
        <div className="bg-nurture-cream-dark/50 p-1 rounded-xl inline-flex">
          <Button
            variant={viewMode === 'for_someone' ? 'default' : 'ghost'}
            onClick={() => setViewMode('for_someone')}
            className={`rounded-lg ${viewMode === 'for_someone' ? 'bg-white shadow-sm text-nurture-navy' : 'text-nurture-navy/70 hover:text-nurture-navy'}`}
            data-testid="mode-for-someone"
          >
            <Users className="w-4 h-4 mr-2" />
            For Someone Else
          </Button>
          <Button
            variant={viewMode === 'to_self' ? 'default' : 'ghost'}
            onClick={() => setViewMode('to_self')}
            className={`rounded-lg ${viewMode === 'to_self' ? 'bg-white shadow-sm text-nurture-navy' : 'text-nurture-navy/70 hover:text-nurture-navy'}`}
            data-testid="mode-to-self"
          >
            <Heart className="w-4 h-4 mr-2" />
            Note to Tomorrow Me
          </Button>
        </div>
      </div>

      {/* For Someone Else View */}
      {viewMode === 'for_someone' && (
        <>
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-handoff-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Handoff
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Handoff Item</DialogTitle>
                </DialogHeader>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="quick">Quick Note</TabsTrigger>
                    <TabsTrigger value="detailed">Detailed (Caregiver)</TabsTrigger>
                  </TabsList>
                  
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Common fields */}
                    <div className="space-y-2">
                      <Label>What needs to be done? *</Label>
                      <Input
                        value={newItem.what}
                        onChange={(e) => setNewItem({ ...newItem, what: e.target.value })}
                        placeholder="Pick up kids at 3pm"
                        required
                        className="rounded-xl"
                        data-testid="handoff-what-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>For whom?</Label>
                        <Select value={newItem.for_whom} onValueChange={(v) => setNewItem({ ...newItem, for_whom: v })}>
                          <SelectTrigger className="rounded-xl" data-testid="handoff-for-whom">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {forWhomOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newItem.priority} onValueChange={(v) => setNewItem({ ...newItem, priority: v })}>
                          <SelectTrigger className="rounded-xl" data-testid="handoff-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {priorities.map(p => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <TabsContent value="quick" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label>When?</Label>
                        <Input
                          value={newItem.when}
                          onChange={(e) => setNewItem({ ...newItem, when: e.target.value })}
                          placeholder="Tonight before 8pm"
                          className="rounded-xl"
                          data-testid="handoff-when-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>How? (Instructions)</Label>
                        <Textarea
                          value={newItem.how}
                          onChange={(e) => setNewItem({ ...newItem, how: e.target.value })}
                          placeholder="Use cold water, gentle cycle..."
                          className="rounded-xl"
                          data-testid="handoff-how-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Special Notes</Label>
                        <Textarea
                          value={newItem.special_notes}
                          onChange={(e) => setNewItem({ ...newItem, special_notes: e.target.value })}
                          placeholder="Emma's PE class is tomorrow..."
                          className="rounded-xl"
                          data-testid="handoff-notes"
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="detailed" className="space-y-4 mt-0">
                      {/* Child's Current State */}
                      <div className="p-4 bg-nurture-cream rounded-xl space-y-4">
                        <h4 className="font-medium text-nurture-navy flex items-center gap-2">
                          <Smile className="w-4 h-4" />
                          Child's Current State
                        </h4>
                        
                        <div className="space-y-2">
                          <Label>Mood</Label>
                          <Select value={newItem.child_mood} onValueChange={(v) => setNewItem({ ...newItem, child_mood: v })}>
                            <SelectTrigger className="rounded-xl bg-white" data-testid="handoff-mood">
                              <SelectValue placeholder="Select mood" />
                            </SelectTrigger>
                            <SelectContent>
                              {moodOptions.map(m => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <UtensilsCrossed className="w-4 h-4" />
                            Last Meal
                          </Label>
                          <Input
                            value={newItem.last_meal}
                            onChange={(e) => setNewItem({ ...newItem, last_meal: e.target.value })}
                            placeholder="Lunch at 12pm - rice & chicken"
                            className="rounded-xl bg-white"
                            data-testid="handoff-last-meal"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Moon className="w-4 h-4" />
                            Nap Info
                          </Label>
                          <Input
                            value={newItem.nap_info}
                            onChange={(e) => setNewItem({ ...newItem, nap_info: e.target.value })}
                            placeholder="Napped 1-3pm, might be sleepy around 7pm"
                            className="rounded-xl bg-white"
                            data-testid="handoff-nap-info"
                          />
                        </div>
                      </div>
                      
                      {/* Medications */}
                      <div className="p-4 bg-red-50 rounded-xl space-y-3">
                        <h4 className="font-medium text-nurture-navy flex items-center gap-2">
                          <Pill className="w-4 h-4 text-red-500" />
                          Medications Given/Due
                        </h4>
                        <div className="flex gap-2">
                          <Input
                            value={newMed}
                            onChange={(e) => setNewMed(e.target.value)}
                            placeholder="Add medication..."
                            className="rounded-xl bg-white"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMedication())}
                            data-testid="handoff-med-input"
                          />
                          <Button type="button" onClick={addMedication} variant="outline" className="rounded-xl">
                            Add
                          </Button>
                        </div>
                        {newItem.medications?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {newItem.medications.map((med, idx) => (
                              <Badge key={idx} variant="secondary" className="bg-white px-3 py-1">
                                {med}
                                <button type="button" onClick={() => removeMedication(idx)} className="ml-2 text-red-500">×</button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Triggers */}
                      <div className="p-4 bg-amber-50 rounded-xl space-y-2">
                        <Label className="flex items-center gap-2 font-medium">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          Triggers / Watch Out For
                        </Label>
                        <Textarea
                          value={newItem.triggers_today}
                          onChange={(e) => setNewItem({ ...newItem, triggers_today: e.target.value })}
                          placeholder="She gets upset if her blue cup isn't available..."
                          className="rounded-xl bg-white"
                          data-testid="handoff-triggers"
                        />
                      </div>
                      
                      {/* Additional Instructions */}
                      <div className="space-y-2">
                        <Label>Additional Instructions</Label>
                        <Textarea
                          value={newItem.how}
                          onChange={(e) => setNewItem({ ...newItem, how: e.target.value })}
                          placeholder="Any other instructions for the caregiver..."
                          className="rounded-xl"
                        />
                      </div>
                    </TabsContent>

                    <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="handoff-submit">
                      Add Handoff
                    </Button>
                  </form>
                </Tabs>
              </DialogContent>
            </Dialog>
          </div>

          {/* Summary */}
          <div className="flex gap-4">
            <Badge className="px-4 py-2 bg-amber-100 text-amber-700">
              <AlertCircle className="w-4 h-4 mr-1" />
              {pendingItems.length} Pending
            </Badge>
            <Badge className="px-4 py-2 bg-green-100 text-green-700">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {completedItems.length} Completed
            </Badge>
          </div>

          {/* Handoff Items */}
          {loading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-nurture-cream-dark rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : items.filter(i => i.handoff_type !== 'to_self').length === 0 ? (
            <Card className="rounded-3xl shadow-nurture">
              <CardContent className="py-16 text-center">
                <Users className="w-16 h-16 mx-auto text-nurture-muted/30 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-nurture-navy mb-2">No handoff items</h3>
                <p className="text-nurture-muted mb-4">Create instructions for helpers or family members</p>
                <p className="text-sm text-nurture-muted max-w-md mx-auto">
                  Perfect for households with domestic helpers, rotating caregivers, or when you need to leave clear instructions.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {/* Pending Items by For Whom */}
              {pendingItems.filter(i => i.handoff_type !== 'to_self').length > 0 && (
                <div>
                  <h2 className="text-xl font-heading font-bold text-nurture-navy mb-4">Pending</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(groupByForWhom(pendingItems.filter(i => i.handoff_type !== 'to_self'))).map(([forWhom, forWhomItems]) => (
                      <motion.div
                        key={forWhom}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <Card className="rounded-3xl shadow-nurture">
                          <CardHeader className="pb-2">
                            <CardTitle className="font-heading text-nurture-navy capitalize">
                              For {forWhom}
                              <Badge variant="outline" className="ml-2">{forWhomItems.length}</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <AnimatePresence>
                                {forWhomItems.map((item) => {
                                  const priorityInfo = getPriorityInfo(item.priority);
                                  return (
                                    <motion.div
                                      key={item.id}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: 10 }}
                                      className={`p-4 rounded-xl group ${
                                        item.priority === 'high' ? 'bg-red-50 border-l-4 border-red-400' : 'bg-nurture-cream/50'
                                      }`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <Checkbox
                                          checked={item.is_completed}
                                          onCheckedChange={() => toggleComplete(item)}
                                          className="mt-1"
                                          data-testid={`handoff-checkbox-${item.id}`}
                                        />
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <p className="font-medium text-nurture-navy">{item.what}</p>
                                            <Badge className={`text-xs ${priorityInfo.color}`}>
                                              {priorityInfo.label}
                                            </Badge>
                                          </div>
                                          {item.when && (
                                            <p className="text-sm text-nurture-terracotta font-medium">{item.when}</p>
                                          )}
                                          {item.how && (
                                            <p className="text-sm text-nurture-muted mt-1">{item.how}</p>
                                          )}
                                          {item.special_notes && (
                                            <p className="text-sm text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded">
                                              Note: {item.special_notes}
                                            </p>
                                          )}
                                          
                                          {/* Caregiver Details */}
                                          {(item.child_mood || item.last_meal || item.nap_info || item.medications?.length > 0 || item.triggers_today) && (
                                            <div className="mt-3 pt-3 border-t border-nurture-cream-dark space-y-2">
                                              {item.child_mood && (
                                                <div className="flex items-center gap-2 text-sm">
                                                  <Smile className="w-4 h-4 text-nurture-muted" />
                                                  <span className="text-nurture-muted">Mood:</span>
                                                  <Badge className={moodOptions.find(m => m.value === item.child_mood)?.color || 'bg-gray-100'}>
                                                    {moodOptions.find(m => m.value === item.child_mood)?.label || item.child_mood}
                                                  </Badge>
                                                </div>
                                              )}
                                              {item.last_meal && (
                                                <div className="flex items-center gap-2 text-sm">
                                                  <UtensilsCrossed className="w-4 h-4 text-nurture-muted" />
                                                  <span className="text-nurture-muted">Last meal:</span>
                                                  <span className="text-nurture-navy">{item.last_meal}</span>
                                                </div>
                                              )}
                                              {item.nap_info && (
                                                <div className="flex items-center gap-2 text-sm">
                                                  <Moon className="w-4 h-4 text-nurture-muted" />
                                                  <span className="text-nurture-muted">Nap:</span>
                                                  <span className="text-nurture-navy">{item.nap_info}</span>
                                                </div>
                                              )}
                                              {item.medications?.length > 0 && (
                                                <div className="flex items-start gap-2 text-sm">
                                                  <Pill className="w-4 h-4 text-red-500 mt-0.5" />
                                                  <span className="text-nurture-muted">Meds:</span>
                                                  <div className="flex flex-wrap gap-1">
                                                    {item.medications.map((med, idx) => (
                                                      <Badge key={idx} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                                        {med}
                                                      </Badge>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                              {item.triggers_today && (
                                                <div className="flex items-start gap-2 text-sm bg-amber-50 p-2 rounded">
                                                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                                  <span className="text-amber-700">{item.triggers_today}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Section 2 Fix: WhatsApp placeholder */}
                                          <div className="mt-3 flex gap-2">
                                            <WhatsAppPlaceholder 
                                              messageText={`Handoff:\n${item.what}${item.when ? `\nWhen: ${item.when}` : ''}${item.how ? `\nHow: ${item.how}` : ''}${item.special_notes ? `\nNote: ${item.special_notes}` : ''}`}
                                              buttonLabel="Send"
                                              size="sm"
                                              variant="outline"
                                              className="text-xs"
                                            />
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => deleteItem(item.id)}
                                          className="opacity-0 group-hover:opacity-100 text-nurture-muted hover:text-red-500"
                                          data-testid={`delete-handoff-${item.id}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </AnimatePresence>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Items */}
              {completedItems.filter(i => i.handoff_type !== 'to_self').length > 0 && (
                <div>
                  <h2 className="text-xl font-heading font-bold text-nurture-muted mb-4">Completed</h2>
                  <div className="space-y-2">
                    {completedItems.filter(i => i.handoff_type !== 'to_self').map((item) => (
                      <div
                        key={item.id}
                        className="p-3 rounded-xl bg-nurture-cream/30 flex items-center gap-3 group"
                      >
                        <Checkbox
                          checked={true}
                          onCheckedChange={() => toggleComplete(item)}
                          data-testid={`handoff-completed-${item.id}`}
                        />
                        <span className="line-through text-nurture-muted flex-1">{item.what}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteItem(item.id)}
                          className="opacity-0 group-hover:opacity-100 h-7 w-7 text-nurture-muted hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Section 3C: Note to Tomorrow Me View */}
      {viewMode === 'to_self' && (
        <div className="space-y-6">
          <Card className="rounded-3xl shadow-nurture bg-gradient-to-br from-purple-50 to-pink-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                Note to Tomorrow Me
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta ml-2">AI</Badge>
              </CardTitle>
              <p className="text-sm text-nurture-muted">
                Write a note to your morning self. Externalise what's in your head so you don't have to carry it.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mood Picker */}
              <div className="space-y-2">
                <Label>How are you feeling tonight?</Label>
                <div className="flex flex-wrap gap-2">
                  {['exhausted', 'tired', 'okay', 'good', 'energized'].map(mood => (
                    <Button
                      key={mood}
                      type="button"
                      variant={selfMood === mood ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelfMood(mood)}
                      className={`rounded-full capitalize ${selfMood === mood ? 'bg-pink-500 hover:bg-pink-600' : ''}`}
                      data-testid={`self-mood-${mood}`}
                    >
                      {mood}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>What's on your mind? (optional)</Label>
                <Textarea
                  value={selfNotes}
                  onChange={(e) => setSelfNotes(e.target.value)}
                  placeholder="Anything you want to acknowledge or let go of..."
                  className="rounded-xl bg-white"
                  rows={3}
                  data-testid="self-notes-input"
                />
              </div>
              
              {/* Tomorrow Priorities */}
              <div className="space-y-2">
                <Label>Tomorrow's priorities (max 3) *</Label>
                <Textarea
                  value={selfPriorities}
                  onChange={(e) => setSelfPriorities(e.target.value)}
                  placeholder="1. School drop-off by 7:30am&#10;2. Dentist appointment at 10&#10;3. Prepare dinner early"
                  className="rounded-xl bg-white"
                  rows={3}
                  data-testid="self-priorities-input"
                />
              </div>
              
              {/* Generate Button */}
              <Button
                onClick={handleWriteSelfNote}
                disabled={writingSelf || !selfMood || !selfPriorities}
                className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-xl"
                data-testid="write-self-note-btn"
              >
                {writingSelf ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Writing your note...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Write My Morning Note
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Generated Note Preview */}
          {generatedSelfNote && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="rounded-3xl shadow-nurture border-2 border-pink-200 bg-gradient-to-br from-white to-pink-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-pink-500" />
                    Your Morning Note
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-white rounded-xl">
                    <p className="text-nurture-navy leading-relaxed whitespace-pre-line" data-testid="generated-self-note">
                      {generatedSelfNote}
                    </p>
                  </div>
                  
                  {/* Reminder Time Picker */}
                  <div className="flex items-center gap-3 p-3 bg-nurture-cream/50 rounded-xl">
                    <Clock className="w-5 h-5 text-nurture-muted" />
                    <span className="text-sm text-nurture-navy">Remind me at:</span>
                    <Input
                      type="time"
                      value={reminderTime}
                      onChange={(e) => setReminderTime(e.target.value)}
                      className="w-28 rounded-lg"
                      data-testid="reminder-time-input"
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => saveSelfNote(true)} 
                      className="bg-nurture-sage hover:bg-nurture-sage-hover rounded-xl"
                      data-testid="save-with-reminder-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Save as Reminder
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => saveSelfNote(false)}
                      className="rounded-xl"
                      data-testid="save-note-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Save Note
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={discardSelfNote}
                      className="rounded-xl"
                      data-testid="discard-self-note-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Discard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Saved Self Notes */}
          {items.filter(i => i.handoff_type === 'to_self').length > 0 && (
            <Card className="rounded-3xl shadow-nurture">
              <CardHeader>
                <CardTitle className="text-nurture-navy">Your Saved Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {items.filter(i => i.handoff_type === 'to_self').map((item) => (
                    <div key={item.id} className="p-4 rounded-xl bg-purple-50 border border-purple-100 group">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-nurture-navy whitespace-pre-line">{item.what}</p>
                          {item.when && (
                            <p className="text-sm text-nurture-muted mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {item.when}
                            </p>
                          )}
                          <p className="text-xs text-nurture-muted mt-1">
                            Created: {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteItem(item.id)}
                          className="opacity-0 group-hover:opacity-100 text-nurture-muted hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default HandoffBoardPage;
